/**
 * Meldingen van de printen-host waarvoor de bundel geen sleutel heeft, in de 10
 * talen van printen.eu. Bewust niet in de i18n-bestanden van de bundel: die zijn
 * gevendord en worden bij een nieuwe versie overschreven.
 *
 * Geen imports: de configurator (op alle producten) gebruikt dit ook.
 */
export interface VstHostMessageSet {
  /** `{list}` = de toegestane aantallen. */
  copies: string;
  rateLimited: string;
  /** `{seconds}` = wachttijd. */
  rateLimitedSeconds: string;
  cartTimeout: string;
  cartFailed: string;
  loadFailed: string;
  /** Tweede bestelklik terwijl de eerste nog toevoegt. */
  busy: string;
  retry: string;
  loading: string;
  unavailableTitle: string;
  contactCta: string;
  /** `{name}` = productnaam. */
  editorTitle: string;
  editorIntro: string;
  /** Label van de winkelwagen-/orderregel bij een ontwerp voor de binnenkant van glas (Ruling P9). */
  glassSideLabel: string;
  /** Waarde van die regel: het snijbestand is al gespiegeld. */
  glassSideInside: string;
  /**
   * Algemene prijsfout van het CUSTOM-pad van de configurator. Komt via de bestel-gate
   * ook in de designer terecht, dus in de taal van de klant (eindreview I2).
   */
  priceFailed: string;
}

/** Meldingen zonder sleutel in de bundel, per taal van printen.eu. */
export const VST_HOST_MESSAGES: Record<string, VstHostMessageSet> = {
  nl: {
    copies: 'Dit aantal kan niet. Kies een van deze aantallen: {list}.',
    rateLimited: 'Te veel prijsaanvragen achter elkaar. Wacht even en probeer het opnieuw.',
    rateLimitedSeconds: 'Te veel prijsaanvragen achter elkaar. Probeer het over {seconds} seconden opnieuw.',
    cartTimeout: 'Het toevoegen aan de winkelwagen duurde te lang. Probeer het opnieuw.',
    cartFailed: 'Het ontwerp kon niet aan de winkelwagen worden toegevoegd. Probeer het opnieuw.',
    loadFailed: 'De ontwerptool kon niet geladen worden. Herlaad de pagina en probeer het opnieuw.',
    busy: "Je bestelling wordt al toegevoegd. Even geduld.",
    retry: "Probeer opnieuw",
    loading: "Ontwerptool laden…",
    unavailableTitle: "Ontwerpen kan nu niet",
    contactCta: "Neem contact op of vraag een offerte aan",
    editorTitle: "{name} ontwerpen",
    editorIntro: "Typ je tekst, kies een lettertype en een kleur, en zie meteen hoe groot het wordt. Wat je hier ziet is precies wat er gesneden wordt.",
    glassSideLabel: "Plaatsing",
    glassSideInside: "Binnenkant glas – gespiegeld snijbestand",
    priceFailed: "Kon prijs niet berekenen. Probeer het opnieuw.",
  },
  de: {
    copies: 'Diese Anzahl ist nicht möglich. Wählen Sie eine dieser Anzahlen: {list}.',
    rateLimited: 'Zu viele Preisanfragen hintereinander. Bitte warten Sie kurz und versuchen Sie es erneut.',
    rateLimitedSeconds: 'Zu viele Preisanfragen hintereinander. Bitte versuchen Sie es in {seconds} Sekunden erneut.',
    cartTimeout: 'Das Hinzufügen zum Warenkorb hat zu lange gedauert. Bitte versuchen Sie es erneut.',
    cartFailed: 'Das Design konnte nicht in den Warenkorb gelegt werden. Bitte versuchen Sie es erneut.',
    loadFailed: 'Das Design-Tool konnte nicht geladen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
    busy: "Ihre Bestellung wird bereits hinzugefügt. Bitte einen Moment Geduld.",
    retry: "Erneut versuchen",
    loading: "Design-Tool wird geladen…",
    unavailableTitle: "Gestalten ist gerade nicht möglich",
    contactCta: "Kontakt aufnehmen oder Angebot anfragen",
    editorTitle: "{name} gestalten",
    editorIntro: "Geben Sie Ihren Text ein, wählen Sie Schrift und Farbe und sehen Sie sofort, wie groß er wird. Was Sie hier sehen, wird genau so geschnitten.",
    glassSideLabel: "Anbringung",
    glassSideInside: "Glasinnenseite – gespiegelte Schneidedatei",
    priceFailed: "Der Preis konnte nicht berechnet werden. Bitte versuchen Sie es erneut.",
  },
  en: {
    copies: 'This quantity is not available. Choose one of these quantities: {list}.',
    rateLimited: 'Too many price requests in a row. Please wait a moment and try again.',
    rateLimitedSeconds: 'Too many price requests in a row. Please try again in {seconds} seconds.',
    cartTimeout: 'Adding to the cart took too long. Please try again.',
    cartFailed: 'The design could not be added to the cart. Please try again.',
    loadFailed: 'The design tool could not be loaded. Please reload the page and try again.',
    busy: "Your order is already being added. Please wait a moment.",
    retry: "Try again",
    loading: "Loading design tool…",
    unavailableTitle: "Designing is not available right now",
    contactCta: "Contact us or request a quote",
    editorTitle: "Design {name}",
    editorIntro: "Type your text, choose a font and a colour, and see right away how big it will be. What you see here is exactly what will be cut.",
    glassSideLabel: "Placement",
    glassSideInside: "Inside of the glass – mirrored cut file",
    priceFailed: "The price could not be calculated. Please try again.",
  },
  fr: {
    copies: "Cette quantité n'est pas disponible. Choisissez l'une de ces quantités : {list}.",
    rateLimited: 'Trop de demandes de prix à la suite. Patientez un instant et réessayez.',
    rateLimitedSeconds: 'Trop de demandes de prix à la suite. Réessayez dans {seconds} secondes.',
    cartTimeout: "L'ajout au panier a pris trop de temps. Veuillez réessayer.",
    cartFailed: "Le design n'a pas pu être ajouté au panier. Veuillez réessayer.",
    loadFailed: 'L’outil de création n’a pas pu être chargé. Rechargez la page et réessayez.',
    busy: "Votre commande est déjà en cours d’ajout. Un instant, s’il vous plaît.",
    retry: "Réessayer",
    loading: "Chargement de l’outil de création…",
    unavailableTitle: "La création n’est pas disponible pour le moment",
    contactCta: "Contactez-nous ou demandez un devis",
    editorTitle: "Créer {name}",
    editorIntro: "Saisissez votre texte, choisissez une police et une couleur, et voyez tout de suite la taille. Ce que vous voyez ici est exactement ce qui sera découpé.",
    glassSideLabel: "Pose",
    glassSideInside: "Intérieur de la vitre – fichier de découpe en miroir",
    priceFailed: "Le prix n'a pas pu être calculé. Veuillez réessayer.",
  },
  da: {
    copies: 'Dette antal er ikke muligt. Vælg et af disse antal: {list}.',
    rateLimited: 'For mange prisforespørgsler i træk. Vent et øjeblik, og prøv igen.',
    rateLimitedSeconds: 'For mange prisforespørgsler i træk. Prøv igen om {seconds} sekunder.',
    cartTimeout: 'Det tog for lang tid at lægge i kurven. Prøv igen.',
    cartFailed: 'Designet kunne ikke lægges i kurven. Prøv igen.',
    loadFailed: 'Designværktøjet kunne ikke indlæses. Genindlæs siden, og prøv igen.',
    busy: "Din bestilling bliver allerede lagt i kurven. Vent et øjeblik.",
    retry: "Prøv igen",
    loading: "Indlæser designværktøj…",
    unavailableTitle: "Design er ikke muligt lige nu",
    contactCta: "Kontakt os eller bed om et tilbud",
    editorTitle: "Design {name}",
    editorIntro: "Skriv din tekst, vælg skrifttype og farve, og se med det samme, hvor stor den bliver. Det, du ser her, er præcis det, der bliver skåret.",
    glassSideLabel: "Placering",
    glassSideInside: "Indersiden af glasset – spejlvendt skærefil",
    priceFailed: "Prisen kunne ikke beregnes. Prøv igen.",
  },
  it: {
    copies: 'Questa quantità non è disponibile. Scegli una di queste quantità: {list}.',
    rateLimited: 'Troppe richieste di prezzo di seguito. Attendi un momento e riprova.',
    rateLimitedSeconds: 'Troppe richieste di prezzo di seguito. Riprova tra {seconds} secondi.',
    cartTimeout: "L'aggiunta al carrello ha richiesto troppo tempo. Riprova.",
    cartFailed: 'Non è stato possibile aggiungere il design al carrello. Riprova.',
    loadFailed: 'Lo strumento di design non è stato caricato. Ricarica la pagina e riprova.',
    busy: "Il tuo ordine è già in fase di aggiunta. Attendi un momento.",
    retry: "Riprova",
    loading: "Caricamento dello strumento di design…",
    unavailableTitle: "Al momento non è possibile progettare",
    contactCta: "Contattaci o richiedi un preventivo",
    editorTitle: "Progetta {name}",
    editorIntro: "Scrivi il tuo testo, scegli un carattere e un colore e vedi subito quanto sarà grande. Quello che vedi qui è esattamente ciò che verrà tagliato.",
    glassSideLabel: "Applicazione",
    glassSideInside: "Lato interno del vetro – file di taglio specchiato",
    priceFailed: "Non è stato possibile calcolare il prezzo. Riprova.",
  },
  es: {
    copies: 'Esta cantidad no está disponible. Elige una de estas cantidades: {list}.',
    rateLimited: 'Demasiadas solicitudes de precio seguidas. Espera un momento y vuelve a intentarlo.',
    rateLimitedSeconds: 'Demasiadas solicitudes de precio seguidas. Vuelve a intentarlo en {seconds} segundos.',
    cartTimeout: 'Añadir a la cesta ha tardado demasiado. Vuelve a intentarlo.',
    cartFailed: 'No se ha podido añadir el diseño a la cesta. Vuelve a intentarlo.',
    loadFailed: 'No se ha podido cargar la herramienta de diseño. Recarga la página y vuelve a intentarlo.',
    busy: "Tu pedido ya se está añadiendo. Espera un momento.",
    retry: "Volver a intentarlo",
    loading: "Cargando la herramienta de diseño…",
    unavailableTitle: "Ahora mismo no se puede diseñar",
    contactCta: "Contacta con nosotros o pide un presupuesto",
    editorTitle: "Diseña {name}",
    editorIntro: "Escribe tu texto, elige una fuente y un color, y ve al instante qué tamaño tendrá. Lo que ves aquí es exactamente lo que se cortará.",
    glassSideLabel: "Colocación",
    glassSideInside: "Cara interior del cristal – archivo de corte en espejo",
    priceFailed: "No se ha podido calcular el precio. Inténtalo de nuevo.",
  },
  pl: {
    copies: 'Ta liczba sztuk jest niedostępna. Wybierz jedną z tych wartości: {list}.',
    rateLimited: 'Zbyt wiele zapytań o cenę z rzędu. Poczekaj chwilę i spróbuj ponownie.',
    rateLimitedSeconds: 'Zbyt wiele zapytań o cenę z rzędu. Spróbuj ponownie za {seconds} s.',
    cartTimeout: 'Dodawanie do koszyka trwało zbyt długo. Spróbuj ponownie.',
    cartFailed: 'Nie udało się dodać projektu do koszyka. Spróbuj ponownie.',
    loadFailed: 'Nie udało się wczytać narzędzia do projektowania. Odśwież stronę i spróbuj ponownie.',
    busy: "Twoje zamówienie jest już dodawane. Chwileczkę.",
    retry: "Spróbuj ponownie",
    loading: "Wczytywanie narzędzia do projektowania…",
    unavailableTitle: "Projektowanie jest teraz niedostępne",
    contactCta: "Skontaktuj się z nami lub poproś o wycenę",
    editorTitle: "Zaprojektuj: {name}",
    editorIntro: "Wpisz tekst, wybierz czcionkę i kolor i od razu zobacz, jak duży będzie. To, co tu widzisz, zostanie dokładnie tak wycięte.",
    glassSideLabel: "Umieszczenie",
    glassSideInside: "Wewnętrzna strona szyby – lustrzany plik do cięcia",
    priceFailed: "Nie udało się obliczyć ceny. Spróbuj ponownie.",
  },
  pt: {
    copies: 'Esta quantidade não está disponível. Escolha uma destas quantidades: {list}.',
    rateLimited: 'Demasiados pedidos de preço seguidos. Aguarde um momento e tente novamente.',
    rateLimitedSeconds: 'Demasiados pedidos de preço seguidos. Tente novamente dentro de {seconds} segundos.',
    cartTimeout: 'A adição ao carrinho demorou demasiado. Tente novamente.',
    cartFailed: 'Não foi possível adicionar o design ao carrinho. Tente novamente.',
    loadFailed: 'Não foi possível carregar a ferramenta de design. Recarregue a página e tente novamente.',
    busy: "A sua encomenda já está a ser adicionada. Aguarde um momento.",
    retry: "Tentar novamente",
    loading: "A carregar a ferramenta de design…",
    unavailableTitle: "Neste momento não é possível criar o design",
    contactCta: "Contacte-nos ou peça um orçamento",
    editorTitle: "Crie {name}",
    editorIntro: "Escreva o seu texto, escolha um tipo de letra e uma cor e veja logo o tamanho. O que vê aqui é exatamente o que será cortado.",
    glassSideLabel: "Aplicação",
    glassSideInside: "Lado interior do vidro – ficheiro de corte espelhado",
    priceFailed: "Não foi possível calcular o preço. Tente novamente.",
  },
  cs: {
    copies: 'Toto množství není k dispozici. Vyberte jedno z těchto množství: {list}.',
    rateLimited: 'Příliš mnoho požadavků na cenu za sebou. Chvíli počkejte a zkuste to znovu.',
    rateLimitedSeconds: 'Příliš mnoho požadavků na cenu za sebou. Zkuste to znovu za {seconds} s.',
    cartTimeout: 'Přidání do košíku trvalo příliš dlouho. Zkuste to znovu.',
    cartFailed: 'Návrh se nepodařilo přidat do košíku. Zkuste to znovu.',
    loadFailed: 'Návrhový nástroj se nepodařilo načíst. Obnovte stránku a zkuste to znovu.',
    busy: "Vaše objednávka se už přidává. Chvíli strpení.",
    retry: "Zkusit znovu",
    loading: "Načítání návrhového nástroje…",
    unavailableTitle: "Navrhování teď není k dispozici",
    contactCta: "Kontaktujte nás nebo si vyžádejte nabídku",
    editorTitle: "Navrhněte: {name}",
    editorIntro: "Napište text, vyberte písmo a barvu a hned uvidíte, jak bude velký. Co vidíte tady, to se přesně vyřeže.",
    glassSideLabel: "Umístění",
    glassSideInside: "Vnitřní strana skla – zrcadlově převrácený řezací soubor",
    priceFailed: "Cenu se nepodařilo vypočítat. Zkuste to znovu.",
  },
};

export function hostMessages(locale: string): VstHostMessageSet {
  const taal = String(locale ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return VST_HOST_MESSAGES[taal] ?? VST_HOST_MESSAGES.en;
}

/** Foutcodes van `/api/vinyl/register` (elke foutreactie draagt er een). */
export type VinylRegisterCode =
  | 'VINYL_VERZOEK_ONGELDIG'
  | 'VINYL_PRODUCT_NIET_GEVONDEN'
  | 'VINYL_GEEN_ONTWERPTOOL'
  | 'VINYL_UPLOAD_NIET_GEVONDEN'
  | 'VINYL_GEEN_PDF'
  | 'VINYL_REGISTRATIE_MISLUKT'
  | 'VINYL_FORMAAT_ONBEKEND'
  | 'VINYL_CONTROLE_NIET_BESCHIKBAAR'
  | 'VINYL_UPLOAD_NIET_VAN_JOU'
  | 'VINYL_SNIJBESTAND_ONGELDIG'
  | 'VINYL_ONTWERP_ONGELDIG'
  | 'VINYL_MAAT_BUITEN_GRENZEN'
  | 'VINYL_CONTOUREN_AFWIJKEND'
  | 'VINYL_TE_VEEL_CONTOUREN'
  | 'VINYL_KLEUR_ONBEKEND'
  | 'VINYL_PDF_NIET_BESCHIKBAAR'
  | 'VINYL_PDF_ONLEESBAAR'
  | 'VINYL_PDF_MAAT_AFWIJKEND';

/**
 * Tekst per foutcode van `/api/vinyl/register`, in de 10 talen van printen.eu (eindreview I2).
 *
 * De route antwoordt met deze tekst in de taal van het verzoek, en de host vertaalt
 * de code nog eens zelf (dan klopt de taal ook als de route een oudere versie is).
 * Plaatshouders: `{maxWidthCm}`/`{maxHeightCm}` (VINYL_MAAT_BUITEN_GRENZEN, uit
 * `maxWidthMm`/`maxHeightMm`) en `{count}`/`{max}` (VINYL_TE_VEEL_CONTOUREN, uit
 * `contourCount`/`maxContours`).
 */
export const VST_REGISTER_ERRORS: Record<string, Record<VinylRegisterCode, string>> = {
  nl: {
    VINYL_VERZOEK_ONGELDIG: "Je ontwerp kon niet worden verstuurd. Vernieuw de pagina en probeer het opnieuw.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Dit product is niet meer beschikbaar.",
    VINYL_GEEN_ONTWERPTOOL: "Voor dit product kun je geen ontwerp maken.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Je ontwerpbestand is niet gevonden. Vernieuw de pagina en bestel het ontwerp opnieuw.",
    VINYL_GEEN_PDF: "Het ontwerpbestand heeft een verkeerd formaat. Vernieuw de pagina en bestel het ontwerp opnieuw.",
    VINYL_REGISTRATIE_MISLUKT: "Je ontwerp kon niet worden opgeslagen. Probeer het over een paar minuten opnieuw.",
    VINYL_FORMAAT_ONBEKEND: "Dit ontwerp is gemaakt met een oude versie van de ontwerptool. Vernieuw de pagina en bestel het ontwerp opnieuw.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Bestellen lukt nu even niet. Probeer het over een paar minuten opnieuw.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Dit ontwerpbestand hoort niet bij jouw sessie. Vernieuw de pagina en bestel het ontwerp opnieuw.",
    VINYL_SNIJBESTAND_ONGELDIG: "Het snijbestand van dit ontwerp kon niet worden gelezen. Vernieuw de pagina en probeer het opnieuw.",
    VINYL_ONTWERP_ONGELDIG: "Dit ontwerp kon niet worden gelezen. Vernieuw de pagina en probeer het opnieuw.",
    VINYL_MAAT_BUITEN_GRENZEN: "Dit ontwerp is groter dan {maxWidthCm} x {maxHeightCm} cm. Maak het ontwerp kleiner.",
    VINYL_CONTOUREN_AFWIJKEND: "Het ontwerp is gewijzigd tijdens het bestellen. Controleer het ontwerp en bestel opnieuw.",
    VINYL_TE_VEEL_CONTOUREN: "Dit ontwerp bestaat uit {count} losse vormen en dat is er meer dan de {max} die wij in een keer kunnen snijden. Maak de tekst korter, of kies een schrijfletter waarbij de letters aan elkaar vastzitten; die worden namelijk samengevoegd tot een vorm.",
    VINYL_KLEUR_ONBEKEND: "Deze kleur is voor dit product niet leverbaar. Kies een andere kleur.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Het ontwerpbestand kon niet worden gecontroleerd. Probeer het over een paar minuten opnieuw.",
    VINYL_PDF_ONLEESBAAR: "Het ontwerpbestand kon niet worden gelezen. Vernieuw de pagina en bestel het ontwerp opnieuw.",
    VINYL_PDF_MAAT_AFWIJKEND: "Het ontwerpbestand past niet bij het ontwerp. Vernieuw de pagina en bestel het ontwerp opnieuw.",
  },
  de: {
    VINYL_VERZOEK_ONGELDIG: "Ihr Design konnte nicht übermittelt werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Dieses Produkt ist nicht mehr verfügbar.",
    VINYL_GEEN_ONTWERPTOOL: "Für dieses Produkt können Sie kein Design erstellen.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Ihre Designdatei wurde nicht gefunden. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
    VINYL_GEEN_PDF: "Die Designdatei hat das falsche Format. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
    VINYL_REGISTRATIE_MISLUKT: "Ihr Design konnte nicht gespeichert werden. Bitte versuchen Sie es in ein paar Minuten erneut.",
    VINYL_FORMAAT_ONBEKEND: "Dieses Design wurde mit einer alten Version des Design-Tools erstellt. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Bestellen ist gerade nicht möglich. Bitte versuchen Sie es in ein paar Minuten erneut.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Diese Designdatei gehört nicht zu Ihrer Sitzung. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
    VINYL_SNIJBESTAND_ONGELDIG: "Die Schneidedatei dieses Designs konnte nicht gelesen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.",
    VINYL_ONTWERP_ONGELDIG: "Dieses Design konnte nicht gelesen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.",
    VINYL_MAAT_BUITEN_GRENZEN: "Dieses Design ist größer als {maxWidthCm} x {maxHeightCm} cm. Bitte verkleinern Sie das Design.",
    VINYL_CONTOUREN_AFWIJKEND: "Das Design wurde während der Bestellung geändert. Bitte prüfen Sie das Design und bestellen Sie erneut.",
    VINYL_TE_VEEL_CONTOUREN: "Dieses Design besteht aus {count} einzelnen Formen, mehr als die {max}, die wir in einem Durchgang schneiden können. Kürzen Sie den Text oder wählen Sie eine Schreibschrift mit verbundenen Buchstaben; diese werden zu einer Form zusammengefügt.",
    VINYL_KLEUR_ONBEKEND: "Diese Farbe ist für dieses Produkt nicht lieferbar. Bitte wählen Sie eine andere Farbe.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Die Designdatei konnte nicht geprüft werden. Bitte versuchen Sie es in ein paar Minuten erneut.",
    VINYL_PDF_ONLEESBAAR: "Die Designdatei konnte nicht gelesen werden. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
    VINYL_PDF_MAAT_AFWIJKEND: "Die Designdatei passt nicht zum Design. Bitte laden Sie die Seite neu und bestellen Sie das Design erneut.",
  },
  en: {
    VINYL_VERZOEK_ONGELDIG: "Your design could not be sent. Please reload the page and try again.",
    VINYL_PRODUCT_NIET_GEVONDEN: "This product is no longer available.",
    VINYL_GEEN_ONTWERPTOOL: "You cannot create a design for this product.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Your design file was not found. Please reload the page and order the design again.",
    VINYL_GEEN_PDF: "The design file has the wrong format. Please reload the page and order the design again.",
    VINYL_REGISTRATIE_MISLUKT: "Your design could not be saved. Please try again in a few minutes.",
    VINYL_FORMAAT_ONBEKEND: "This design was made with an old version of the design tool. Please reload the page and order the design again.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Ordering is not possible right now. Please try again in a few minutes.",
    VINYL_UPLOAD_NIET_VAN_JOU: "This design file does not belong to your session. Please reload the page and order the design again.",
    VINYL_SNIJBESTAND_ONGELDIG: "The cut file of this design could not be read. Please reload the page and try again.",
    VINYL_ONTWERP_ONGELDIG: "This design could not be read. Please reload the page and try again.",
    VINYL_MAAT_BUITEN_GRENZEN: "This design is larger than {maxWidthCm} x {maxHeightCm} cm. Please make the design smaller.",
    VINYL_CONTOUREN_AFWIJKEND: "The design changed while you were ordering. Please check the design and order again.",
    VINYL_TE_VEEL_CONTOUREN: "This design consists of {count} separate shapes, more than the {max} we can cut in one go. Shorten the text, or choose a script font with connected letters; those are merged into one shape.",
    VINYL_KLEUR_ONBEKEND: "This colour is not available for this product. Please choose another colour.",
    VINYL_PDF_NIET_BESCHIKBAAR: "The design file could not be checked. Please try again in a few minutes.",
    VINYL_PDF_ONLEESBAAR: "The design file could not be read. Please reload the page and order the design again.",
    VINYL_PDF_MAAT_AFWIJKEND: "The design file does not match the design. Please reload the page and order the design again.",
  },
  fr: {
    VINYL_VERZOEK_ONGELDIG: "Votre création n'a pas pu être envoyée. Veuillez recharger la page et réessayer.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Ce produit n'est plus disponible.",
    VINYL_GEEN_ONTWERPTOOL: "Vous ne pouvez pas créer de design pour ce produit.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Votre fichier de création est introuvable. Veuillez recharger la page et commander à nouveau le design.",
    VINYL_GEEN_PDF: "Le fichier de création n'a pas le bon format. Veuillez recharger la page et commander à nouveau le design.",
    VINYL_REGISTRATIE_MISLUKT: "Votre création n'a pas pu être enregistrée. Veuillez réessayer dans quelques minutes.",
    VINYL_FORMAAT_ONBEKEND: "Cette création a été réalisée avec une ancienne version de l'outil de design. Veuillez recharger la page et commander à nouveau le design.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "La commande n'est pas possible pour le moment. Veuillez réessayer dans quelques minutes.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Ce fichier de création n'appartient pas à votre session. Veuillez recharger la page et commander à nouveau le design.",
    VINYL_SNIJBESTAND_ONGELDIG: "Le fichier de découpe de cette création n'a pas pu être lu. Veuillez recharger la page et réessayer.",
    VINYL_ONTWERP_ONGELDIG: "Cette création n'a pas pu être lue. Veuillez recharger la page et réessayer.",
    VINYL_MAAT_BUITEN_GRENZEN: "Cette création dépasse {maxWidthCm} x {maxHeightCm} cm. Veuillez réduire la taille du design.",
    VINYL_CONTOUREN_AFWIJKEND: "La création a été modifiée pendant la commande. Vérifiez le design et commandez à nouveau.",
    VINYL_TE_VEEL_CONTOUREN: "Cette création comporte {count} formes séparées, soit plus que les {max} que nous pouvons découper en une fois. Raccourcissez le texte ou choisissez une police manuscrite aux lettres liées ; elles sont fusionnées en une seule forme.",
    VINYL_KLEUR_ONBEKEND: "Cette couleur n'est pas disponible pour ce produit. Veuillez choisir une autre couleur.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Le fichier de création n'a pas pu être vérifié. Veuillez réessayer dans quelques minutes.",
    VINYL_PDF_ONLEESBAAR: "Le fichier de création n'a pas pu être lu. Veuillez recharger la page et commander à nouveau le design.",
    VINYL_PDF_MAAT_AFWIJKEND: "Le fichier de création ne correspond pas au design. Veuillez recharger la page et commander à nouveau le design.",
  },
  da: {
    VINYL_VERZOEK_ONGELDIG: "Dit design kunne ikke sendes. Genindlæs siden, og prøv igen.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Dette produkt er ikke længere tilgængeligt.",
    VINYL_GEEN_ONTWERPTOOL: "Du kan ikke lave et design til dette produkt.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Din designfil blev ikke fundet. Genindlæs siden, og bestil designet igen.",
    VINYL_GEEN_PDF: "Designfilen har det forkerte format. Genindlæs siden, og bestil designet igen.",
    VINYL_REGISTRATIE_MISLUKT: "Dit design kunne ikke gemmes. Prøv igen om et par minutter.",
    VINYL_FORMAAT_ONBEKEND: "Dette design er lavet med en gammel version af designværktøjet. Genindlæs siden, og bestil designet igen.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Det er ikke muligt at bestille lige nu. Prøv igen om et par minutter.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Denne designfil hører ikke til din session. Genindlæs siden, og bestil designet igen.",
    VINYL_SNIJBESTAND_ONGELDIG: "Skærefilen til dette design kunne ikke læses. Genindlæs siden, og prøv igen.",
    VINYL_ONTWERP_ONGELDIG: "Dette design kunne ikke læses. Genindlæs siden, og prøv igen.",
    VINYL_MAAT_BUITEN_GRENZEN: "Dette design er større end {maxWidthCm} x {maxHeightCm} cm. Gør designet mindre.",
    VINYL_CONTOUREN_AFWIJKEND: "Designet blev ændret under bestillingen. Kontrollér designet, og bestil igen.",
    VINYL_TE_VEEL_CONTOUREN: "Dette design består af {count} separate former, flere end de {max}, vi kan skære på én gang. Gør teksten kortere, eller vælg en skrifttype med sammenhængende bogstaver; de bliver slået sammen til én form.",
    VINYL_KLEUR_ONBEKEND: "Denne farve kan ikke leveres til dette produkt. Vælg en anden farve.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Designfilen kunne ikke kontrolleres. Prøv igen om et par minutter.",
    VINYL_PDF_ONLEESBAAR: "Designfilen kunne ikke læses. Genindlæs siden, og bestil designet igen.",
    VINYL_PDF_MAAT_AFWIJKEND: "Designfilen passer ikke til designet. Genindlæs siden, og bestil designet igen.",
  },
  it: {
    VINYL_VERZOEK_ONGELDIG: "Non è stato possibile inviare il tuo design. Ricarica la pagina e riprova.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Questo prodotto non è più disponibile.",
    VINYL_GEEN_ONTWERPTOOL: "Per questo prodotto non puoi creare un design.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Il file del tuo design non è stato trovato. Ricarica la pagina e ordina di nuovo il design.",
    VINYL_GEEN_PDF: "Il file del design ha un formato errato. Ricarica la pagina e ordina di nuovo il design.",
    VINYL_REGISTRATIE_MISLUKT: "Non è stato possibile salvare il tuo design. Riprova tra qualche minuto.",
    VINYL_FORMAAT_ONBEKEND: "Questo design è stato creato con una vecchia versione dello strumento di design. Ricarica la pagina e ordina di nuovo il design.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Al momento non è possibile ordinare. Riprova tra qualche minuto.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Questo file di design non appartiene alla tua sessione. Ricarica la pagina e ordina di nuovo il design.",
    VINYL_SNIJBESTAND_ONGELDIG: "Non è stato possibile leggere il file di taglio di questo design. Ricarica la pagina e riprova.",
    VINYL_ONTWERP_ONGELDIG: "Non è stato possibile leggere questo design. Ricarica la pagina e riprova.",
    VINYL_MAAT_BUITEN_GRENZEN: "Questo design è più grande di {maxWidthCm} x {maxHeightCm} cm. Rendi il design più piccolo.",
    VINYL_CONTOUREN_AFWIJKEND: "Il design è stato modificato durante l'ordine. Controlla il design e ordina di nuovo.",
    VINYL_TE_VEEL_CONTOUREN: "Questo design è composto da {count} forme separate, più delle {max} che possiamo tagliare in una volta. Accorcia il testo oppure scegli un carattere corsivo con lettere unite: vengono fuse in un'unica forma.",
    VINYL_KLEUR_ONBEKEND: "Questo colore non è disponibile per questo prodotto. Scegli un altro colore.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Non è stato possibile controllare il file del design. Riprova tra qualche minuto.",
    VINYL_PDF_ONLEESBAAR: "Non è stato possibile leggere il file del design. Ricarica la pagina e ordina di nuovo il design.",
    VINYL_PDF_MAAT_AFWIJKEND: "Il file del design non corrisponde al design. Ricarica la pagina e ordina di nuovo il design.",
  },
  es: {
    VINYL_VERZOEK_ONGELDIG: "No se ha podido enviar tu diseño. Recarga la página e inténtalo de nuevo.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Este producto ya no está disponible.",
    VINYL_GEEN_ONTWERPTOOL: "No puedes crear un diseño para este producto.",
    VINYL_UPLOAD_NIET_GEVONDEN: "No se ha encontrado el archivo de tu diseño. Recarga la página y vuelve a pedir el diseño.",
    VINYL_GEEN_PDF: "El archivo del diseño tiene un formato incorrecto. Recarga la página y vuelve a pedir el diseño.",
    VINYL_REGISTRATIE_MISLUKT: "No se ha podido guardar tu diseño. Inténtalo de nuevo dentro de unos minutos.",
    VINYL_FORMAAT_ONBEKEND: "Este diseño se creó con una versión antigua de la herramienta de diseño. Recarga la página y vuelve a pedir el diseño.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Ahora mismo no es posible hacer el pedido. Inténtalo de nuevo dentro de unos minutos.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Este archivo de diseño no pertenece a tu sesión. Recarga la página y vuelve a pedir el diseño.",
    VINYL_SNIJBESTAND_ONGELDIG: "No se ha podido leer el archivo de corte de este diseño. Recarga la página e inténtalo de nuevo.",
    VINYL_ONTWERP_ONGELDIG: "No se ha podido leer este diseño. Recarga la página e inténtalo de nuevo.",
    VINYL_MAAT_BUITEN_GRENZEN: "Este diseño es mayor de {maxWidthCm} x {maxHeightCm} cm. Haz el diseño más pequeño.",
    VINYL_CONTOUREN_AFWIJKEND: "El diseño ha cambiado durante el pedido. Revisa el diseño y vuelve a pedirlo.",
    VINYL_TE_VEEL_CONTOUREN: "Este diseño tiene {count} formas sueltas, más de las {max} que podemos cortar de una vez. Acorta el texto o elige una letra caligráfica con las letras unidas; se combinan en una sola forma.",
    VINYL_KLEUR_ONBEKEND: "Este color no está disponible para este producto. Elige otro color.",
    VINYL_PDF_NIET_BESCHIKBAAR: "No se ha podido comprobar el archivo del diseño. Inténtalo de nuevo dentro de unos minutos.",
    VINYL_PDF_ONLEESBAAR: "No se ha podido leer el archivo del diseño. Recarga la página y vuelve a pedir el diseño.",
    VINYL_PDF_MAAT_AFWIJKEND: "El archivo del diseño no coincide con el diseño. Recarga la página y vuelve a pedir el diseño.",
  },
  pl: {
    VINYL_VERZOEK_ONGELDIG: "Nie udało się wysłać Twojego projektu. Odśwież stronę i spróbuj ponownie.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Ten produkt nie jest już dostępny.",
    VINYL_GEEN_ONTWERPTOOL: "Dla tego produktu nie można utworzyć projektu.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Nie znaleziono pliku Twojego projektu. Odśwież stronę i zamów projekt ponownie.",
    VINYL_GEEN_PDF: "Plik projektu ma nieprawidłowy format. Odśwież stronę i zamów projekt ponownie.",
    VINYL_REGISTRATIE_MISLUKT: "Nie udało się zapisać Twojego projektu. Spróbuj ponownie za kilka minut.",
    VINYL_FORMAAT_ONBEKEND: "Ten projekt został utworzony w starej wersji narzędzia do projektowania. Odśwież stronę i zamów projekt ponownie.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Złożenie zamówienia jest teraz niemożliwe. Spróbuj ponownie za kilka minut.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Ten plik projektu nie należy do Twojej sesji. Odśwież stronę i zamów projekt ponownie.",
    VINYL_SNIJBESTAND_ONGELDIG: "Nie udało się odczytać pliku cięcia tego projektu. Odśwież stronę i spróbuj ponownie.",
    VINYL_ONTWERP_ONGELDIG: "Nie udało się odczytać tego projektu. Odśwież stronę i spróbuj ponownie.",
    VINYL_MAAT_BUITEN_GRENZEN: "Ten projekt jest większy niż {maxWidthCm} x {maxHeightCm} cm. Zmniejsz projekt.",
    VINYL_CONTOUREN_AFWIJKEND: "Projekt zmienił się podczas składania zamówienia. Sprawdź projekt i zamów ponownie.",
    VINYL_TE_VEEL_CONTOUREN: "Ten projekt składa się z {count} oddzielnych kształtów, czyli więcej niż {max}, które możemy wyciąć za jednym razem. Skróć tekst lub wybierz pismo odręczne z połączonymi literami; zostaną one scalone w jeden kształt.",
    VINYL_KLEUR_ONBEKEND: "Ten kolor jest niedostępny dla tego produktu. Wybierz inny kolor.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Nie udało się sprawdzić pliku projektu. Spróbuj ponownie za kilka minut.",
    VINYL_PDF_ONLEESBAAR: "Nie udało się odczytać pliku projektu. Odśwież stronę i zamów projekt ponownie.",
    VINYL_PDF_MAAT_AFWIJKEND: "Plik projektu nie pasuje do projektu. Odśwież stronę i zamów projekt ponownie.",
  },
  pt: {
    VINYL_VERZOEK_ONGELDIG: "Não foi possível enviar o seu design. Recarregue a página e tente novamente.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Este produto já não está disponível.",
    VINYL_GEEN_ONTWERPTOOL: "Não é possível criar um design para este produto.",
    VINYL_UPLOAD_NIET_GEVONDEN: "O ficheiro do seu design não foi encontrado. Recarregue a página e encomende o design novamente.",
    VINYL_GEEN_PDF: "O ficheiro do design tem o formato errado. Recarregue a página e encomende o design novamente.",
    VINYL_REGISTRATIE_MISLUKT: "Não foi possível guardar o seu design. Tente novamente dentro de alguns minutos.",
    VINYL_FORMAAT_ONBEKEND: "Este design foi criado com uma versão antiga da ferramenta de design. Recarregue a página e encomende o design novamente.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "De momento não é possível encomendar. Tente novamente dentro de alguns minutos.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Este ficheiro de design não pertence à sua sessão. Recarregue a página e encomende o design novamente.",
    VINYL_SNIJBESTAND_ONGELDIG: "Não foi possível ler o ficheiro de corte deste design. Recarregue a página e tente novamente.",
    VINYL_ONTWERP_ONGELDIG: "Não foi possível ler este design. Recarregue a página e tente novamente.",
    VINYL_MAAT_BUITEN_GRENZEN: "Este design é maior do que {maxWidthCm} x {maxHeightCm} cm. Reduza o tamanho do design.",
    VINYL_CONTOUREN_AFWIJKEND: "O design foi alterado durante a encomenda. Verifique o design e encomende novamente.",
    VINYL_TE_VEEL_CONTOUREN: "Este design tem {count} formas separadas, mais do que as {max} que conseguimos cortar de uma vez. Encurte o texto ou escolha uma letra manuscrita com as letras ligadas; essas são unidas numa só forma.",
    VINYL_KLEUR_ONBEKEND: "Esta cor não está disponível para este produto. Escolha outra cor.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Não foi possível verificar o ficheiro do design. Tente novamente dentro de alguns minutos.",
    VINYL_PDF_ONLEESBAAR: "Não foi possível ler o ficheiro do design. Recarregue a página e encomende o design novamente.",
    VINYL_PDF_MAAT_AFWIJKEND: "O ficheiro do design não corresponde ao design. Recarregue a página e encomende o design novamente.",
  },
  cs: {
    VINYL_VERZOEK_ONGELDIG: "Váš návrh se nepodařilo odeslat. Obnovte stránku a zkuste to znovu.",
    VINYL_PRODUCT_NIET_GEVONDEN: "Tento produkt již není k dispozici.",
    VINYL_GEEN_ONTWERPTOOL: "Pro tento produkt nelze vytvořit návrh.",
    VINYL_UPLOAD_NIET_GEVONDEN: "Soubor vašeho návrhu nebyl nalezen. Obnovte stránku a objednejte návrh znovu.",
    VINYL_GEEN_PDF: "Soubor návrhu má nesprávný formát. Obnovte stránku a objednejte návrh znovu.",
    VINYL_REGISTRATIE_MISLUKT: "Váš návrh se nepodařilo uložit. Zkuste to znovu za několik minut.",
    VINYL_FORMAAT_ONBEKEND: "Tento návrh byl vytvořen ve staré verzi návrhového nástroje. Obnovte stránku a objednejte návrh znovu.",
    VINYL_CONTROLE_NIET_BESCHIKBAAR: "Objednat teď bohužel nelze. Zkuste to znovu za několik minut.",
    VINYL_UPLOAD_NIET_VAN_JOU: "Tento soubor návrhu nepatří k vaší relaci. Obnovte stránku a objednejte návrh znovu.",
    VINYL_SNIJBESTAND_ONGELDIG: "Řezací soubor tohoto návrhu se nepodařilo načíst. Obnovte stránku a zkuste to znovu.",
    VINYL_ONTWERP_ONGELDIG: "Tento návrh se nepodařilo načíst. Obnovte stránku a zkuste to znovu.",
    VINYL_MAAT_BUITEN_GRENZEN: "Tento návrh je větší než {maxWidthCm} x {maxHeightCm} cm. Návrh zmenšete.",
    VINYL_CONTOUREN_AFWIJKEND: "Návrh se během objednávání změnil. Zkontrolujte návrh a objednejte znovu.",
    VINYL_TE_VEEL_CONTOUREN: "Tento návrh se skládá z {count} samostatných tvarů, což je více než {max}, které dokážeme vyřezat najednou. Zkraťte text nebo zvolte psací písmo se spojenými písmeny; ta se sloučí do jednoho tvaru.",
    VINYL_KLEUR_ONBEKEND: "Tato barva není pro tento produkt k dispozici. Zvolte jinou barvu.",
    VINYL_PDF_NIET_BESCHIKBAAR: "Soubor návrhu se nepodařilo zkontrolovat. Zkuste to znovu za několik minut.",
    VINYL_PDF_ONLEESBAAR: "Soubor návrhu se nepodařilo načíst. Obnovte stránku a objednejte návrh znovu.",
    VINYL_PDF_MAAT_AFWIJKEND: "Soubor návrhu neodpovídá návrhu. Obnovte stránku a objednejte návrh znovu.",
  },
};

function taalVan(locale: string): string {
  return String(locale ?? '').trim().toLowerCase().split(/[-_]/)[0];
}

const cm = (mm: unknown) => {
  const n = Number(mm);
  return Number.isFinite(n) && n > 0 ? String(Math.round(n) / 10) : null;
};
const geheel = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? String(Math.round(n)) : null;
};

/**
 * De tekst voor de klant bij een foutantwoord van `/api/vinyl/register`, of `null`
 * als de code onbekend is of een plaatshouder geen waarde heeft (de aanroeper valt
 * dan terug op `error` uit het antwoord).
 */
export function registerErrorText(locale: string, data: Record<string, unknown> | null | undefined): string | null {
  const code = typeof data?.code === 'string' ? data.code : '';
  const tabel = VST_REGISTER_ERRORS[taalVan(locale)] ?? VST_REGISTER_ERRORS.en;
  if (!Object.prototype.hasOwnProperty.call(tabel, code)) return null;
  const waarden: Record<string, string | null> = {
    maxWidthCm: cm(data?.maxWidthMm),
    maxHeightCm: cm(data?.maxHeightMm),
    count: geheel(data?.contourCount),
    max: geheel(data?.maxContours),
  };
  let ontbreekt = false;
  const tekst = tabel[code as VinylRegisterCode].replace(/\{(maxWidthCm|maxHeightCm|count|max)\}/g, (_, sleutel: string) => {
    const waarde = waarden[sleutel];
    if (waarde === null) ontbreekt = true;
    return waarde ?? '';
  });
  return ontbreekt ? null : tekst;
}
